import { useEffect, useMemo, useState } from 'react';
import useEvent from '../../hooks/useEvent';
import { downloadCrossOriginFile, formatMediaDownloadName } from '@/utils/formatFileName';
import { trpc } from '@/utils/trpc';
import { captureException } from '@sentry/nextjs';
import { EVENTS } from '@/utils/constants';
import useInApp from '@/hooks/useInApp';

const downloadMap = {
  social: 'small_image_url',
  web: 'large_image_url',
  print: 'full_image_url',
};

const DownloadMediaButtonContainer = ({
  media,
  hoverShow,
  resolution = 'web',
  children,
  className = '',
}) => {
  const [downloadLink, setDownloadLink] = useState(null);
  const downloadAssetFromS3Mutation = trpc.packs.downloadAssetFromS3.useMutation();
  const { capture } = useEvent();
  const { inPublicPage } = useInApp();

  const packId = useMemo(() => {
    if (media.type === 'video') {
      return media.id;
    } else if (media.type === 'clip') {
      return media.pack_id;
    } else if (media.type === 'echo') {
      return media.pack_id;
    } else {
      if (media.pack_id) {
        return media.pack_id;
      } else {
        //Handle media get from Algolia
        try {
          const pack = JSON.parse(media.pack);
          return pack.id;
        } catch (e) {
          return null;
        }
      }
    }
  }, [media]);

  const { data: project } = trpc.packs.getPackById.useQuery(packId, {
    enabled: !!packId && !inPublicPage,
  });

  const mediaType = useMemo(() => {
    if (media.type === 'video') {
      return 'video';
    } else if (media.type === 'clip') {
      return 'clip';
    } else if (media.type === 'echo') {
      return 'echo';
    } else {
      return 'photo';
    }
  }, [media]);

  useEffect(() => {
    let mediaDownloadLink = null;
    if (mediaType === 'photo') {
      mediaDownloadLink = media?.imgix?.downloads?.[downloadMap[resolution]] ?? null;
    }
    if (mediaType === 'video') {
      mediaDownloadLink = media?.zip_file ?? null;
    }
    if (mediaType === 'clip') {
      mediaDownloadLink = media?.clip_file;
    }
    if (mediaType === 'echo') {
      mediaDownloadLink = media?.imgix?.downloads?.[downloadMap[resolution]] ?? null;
    }
    setDownloadLink(mediaDownloadLink);
  }, [media, resolution, mediaType]);

  const event = useMemo(() => {
    if (mediaType === 'video') {
      return EVENTS.downloadedVideo;
    } else if (mediaType === 'clip') {
      return EVENTS.downloadedClip;
    } else if (mediaType === 'echo') {
      return EVENTS.downloadedEcho;
    } else {
      return EVENTS.downloadedPhoto;
    }
  }, [mediaType]);

  const captureDownload = async (e) => {
    e.stopPropagation();
    const photo_id = mediaType === 'photo' || mediaType === 'echo' ? media.id : undefined;
    const pack_id =
      mediaType === 'echo' ? undefined : mediaType === 'video' ? media.id : media.pack_id;
    const clip_id = mediaType === 'clip' ? media.id : undefined;
    const props = mediaType === 'photo' ? [{ key: 'resolution', value: resolution }] : [];

    capture({
      event,
      photo_id,
      pack_id,
      clip_id,
      properties: props,
    });

    // trigger download
    if (downloadLink) {
      try {
        const fileName = formatMediaDownloadName(
          project,
          { ...media, type: mediaType },
          downloadLink
        );
        if (mediaType === 'clip') {
          const zipUrl = await downloadAssetFromS3Mutation.mutateAsync({
            assetUrl: downloadLink,
            fileName: fileName,
          });
          const link = document.createElement('a');
          link.href = zipUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          await downloadCrossOriginFile(downloadLink, fileName);
        }
      } catch (err) {
        captureException(new Error(err.message ?? 'Error downloading media'));
      }
    }
  };

  if (!downloadLink) return null;

  return (
    <div
      onClick={(e) => captureDownload(e)}
      className={className ? className : `grid ${hoverShow ? 'opacity-100' : 'opacity-0'}`}
    >
      {children}
    </div>
  );
};

export default DownloadMediaButtonContainer;
